import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, map, combineLatest, distinctUntilChanged, tap, fromEvent, filter, withLatestFrom } from 'rxjs';
import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { strToTitleCase } from 'src/app/js';
import { LibModule } from 'src/app/lib.ts/lib.module';

import { SharedModule } from 'src/app/shared/shared.module';

import { SubSink } from 'subsink';

import { JobMutateObjectActions } from './mutate-object.actions';
import { mutateObjectFeature } from './mutate-object.feature';

import { jobMutatorsInitialState } from './mutate-object.state';

export interface Step {
  name?: string;
  reviewValue?: string;
  templateId?: string;
  editable: boolean;
  disabled?: boolean;
  invalid?: boolean;
  disableNextOnEnter?: boolean;
};

@Component({
  selector: 'app-job-mutate-object',
  standalone: true,
  imports: [
    SharedModule,
    LibModule,
    ReactiveFormsModule,
    FreyaCommonModule,
],
  templateUrl: './mutate-object.component.html',
  styleUrls: ['./mutate-object.component.scss'],
  providers: [ConfirmationService]
})
export class JobMutateObjectComponent implements OnInit, OnDestroy {

  private steps$ = new BehaviorSubject<Step[]>([]);

  @Input() set steps(value: Step[]) {
    this.steps$.next(value);
  }

  @Input() mutateObjectType: keyof (typeof jobMutatorsInitialState);

  @Input() templates: Record<string, TemplateRef<any>>;

  // Visual Modifiers
  @Input() reviewButtonText: string;
  @Input() disclaimer: string; // Text that appears in the review section above the button

  // 200px from PrimeNg - https://primeng.org/listbox#api.listbox.props.scrollHeight
  @Input() scrollHeight = '200px'; // Deafult Height of the Dialog 

  stepMap: Record<string, Step>;

  // Variables used for Functionality
  onlyShowEditableItems = false; // When True any items that cannot be edited are filtered out

  removeListeners: () => void;

  // Used to set focus on first input
  private _inputSelectors = {
    inputText: 'input.p-inputtext',
    dropdown: 'p-dropdown',
    checkbox: 'p-checkbox',
    multiSelect: 'p-multiselect',
    chips: 'p-chips',
    colorPicker: 'p-colorpicker',
    textarea: 'textarea',
    calendar: 'p-calendar',
    autocomplete: 'p-autocomplete',
    inputNumber: 'p-inputnumber',
    inputSwitch: 'p-inputswitch'
  };

  private _subs = new SubSink();

  constructor(
    private viewContainerRef: ViewContainerRef,
    public store: Store,
    @Inject(DOCUMENT) private doc: Document,
  ) { }

  public actions = JobMutateObjectActions;

  public viewModel$ = combineLatest(this.store.select(mutateObjectFeature.selectMutateObjectDialog), this.steps$).pipe(map(([
    {
      activeMutator,
      activeStep,
      footerButtons,
      mutateType,
      loading,
    },
    steps
  ]) => {

    const title = strToTitleCase(`${mutateType} ${activeMutator}`);

    const editableSteps = steps.filter((s) => s.editable);

    const activeStepIndex = editableSteps.findIndex((s) => s.name === activeStep);

    const currentStep = editableSteps[activeStepIndex];

    const previousStep = editableSteps[activeStepIndex - 1];

    const nextStep = editableSteps[activeStepIndex + 1];

    const invalid = steps.some((s) => s.invalid);

    return {
      title,
      visible: activeMutator === this.mutateObjectType,
      activeStep,
      footerButtons,
      mutateType,
      isReviewStep: activeStep === 'review',
      loading,
      currentStep,
      previousStep,
      nextStep,
      invalid,
      steps,
    };
  }));

  ngOnInit(): void {

    this._subs.sink = this.viewModel$.pipe(
      map((viewModel) => viewModel.currentStep),
      distinctUntilChanged(),
      tap(() => {
        queueMicrotask(() => this._setFocusOnFirstInput());
      }),
    ).subscribe();

    this._subs.sink = fromEvent<KeyboardEvent>(this.doc, 'keydown')
    .pipe(
      filter(({ key }) => key === 'Enter' || key === 'Escape'),
      map(({ key }) => key as 'Enter' | 'Escape'),
      withLatestFrom(this.viewModel$),
      filter(([ _, viewModel ]) => viewModel.visible),
      tap(([ key, { isReviewStep, nextStep, currentStep } ]) => {


        if (key === 'Escape') {
          this.store.dispatch(this.actions.escapeKeyPressed());
          return;
        }

        if (this._isDropdownActive()) { return; }

        if (isReviewStep) {
          this.store.dispatch(this.actions.enterKeyPressed({ mutate: true }));
          return;
        }

        if (currentStep?.disableNextOnEnter) { return; }

        if (nextStep) {
          this.store.dispatch(this.actions.enterKeyPressed({ goToStep: nextStep.name }));
          return;
        }

        this.store.dispatch(this.actions.enterKeyPressed({ goToStep: 'review' }));
      }),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  _isDropdownActive(): boolean {
    const activeElement = document.activeElement;
    return activeElement && activeElement.classList.contains('p-dropdown-label');
  }

  private _setFocusOnFirstInput() {

    const parentElement: Element = this.viewContainerRef.element.nativeElement;
    const firstInput = parentElement.querySelector(`:is(${Object.values(this._inputSelectors).join(', ')})`);

    if (!firstInput) { return; };

    this._applyFocus(firstInput);
  }

  private _applyFocus(input: ElementRef['nativeElement']) {

    if (input.hasAttribute('pinputtext') || input.hasAttribute('pinputtextarea')) {
      input.focus();
      return;
    }

    switch(input.tagName) {
      case 'P-AUTOCOMPLETE':

        const inputButtons = input.getElementsByTagName('button');

        if (inputButtons.length > 0) {
          inputButtons[0].click();
          return;
        }

        const innerAutocompleteInputs = input.getElementsByTagName('input');

        if (!innerAutocompleteInputs.length) { return; }

        innerAutocompleteInputs[0].focus();

        return;
      case 'P-MULTISELECT':
        const trigger = input.querySelector('.p-multiselect-trigger');
        if (!trigger) { return; }
        setTimeout(()=>{
          trigger.click();
        },150);
        return;
      case 'P-COLORPICKER':
        const clickableInputs = input.getElementsByTagName('input');
        if (!clickableInputs.length) { return; };
        clickableInputs[0].click();
        return;
      default:
        const innerInputs = input.getElementsByTagName('input');
        if (!innerInputs.length) { return; };
        innerInputs[0].focus();
    }
  }
}
